<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '../stores/useAuthStore';
import defaultAvatar from '@/assets/default-avatar.png';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';
import { auth_url, app_url } from '../config/config';

const authStore = useAuthStore();
const route = useRoute();

const isFetchingUser = ref(false);
const cookiesAccepted = computed(() => authStore.cookiesAccepted);

const fetchUser = async () => {
  if (isFetchingUser.value || !authStore.isAuthenticated) return;
  isFetchingUser.value = true;
  try {
    await authStore.fetchCurrentUser();
  } catch (error) {
    console.error('Fetch user error:', error);
    handleLogout();
  } finally {
    isFetchingUser.value = false;
  }
};

const handleLogout = () => {
  authStore.logout();
};

const openCookieBanner = () => {
  window.dispatchEvent(new Event('open-cookie-banner'));
};

const goToProfile = () => {
  authStore.closeAuthModal();
  window.location.href = `${auth_url}/profile?redirect=${encodeURIComponent(app_url)}`;
};

onMounted(() => {
  authStore.fetchLicense();
});

watch(() => route.path, async () => {
  if (authStore.token && !isFetchingUser.value) {
    await fetchUser();
    await authStore.fetchLicense();
  }
});

watch(() => authStore.token, async (newToken, oldToken) => {
  if (isFetchingUser.value) return;
  if (!newToken && oldToken) {
    handleLogout();
  } else if (newToken) {
    await fetchUser();
    await authStore.fetchLicense();
  }
}, { immediate: true });
</script>

<template>
  <div class="dh-wrapper">

    <!-- ── En-tête DartsHub ── -->
    <div class="dh-header">
      <svg class="dh-target-bg" viewBox="-140 -140 280 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="0" cy="0" r="140" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.35)" stroke-width="1.2" />
        <circle cx="0" cy="0" r="124" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
        <circle cx="0" cy="0" r="82" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.35)" stroke-width="1" />
        <circle cx="0" cy="0" r="72" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
        <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
        <circle cx="0" cy="0" r="6" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(0)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(18)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(36)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(54)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(72)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(90)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(108)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(126)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(144)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(162)" />
      </svg>

      <div class="dh-header-content">
        <div class="dh-brand">
          <img :src="dartsHubLogo" alt="DartsHub" class="dh-brand-logo" />
        </div>
        <div class="dh-accent-bar">
          <span></span><span></span><span></span>
        </div>
        <p class="dh-subtitle">
          {{ authStore.isAuthenticated ? 'Votre espace membre' : 'Connexion via DartsHub' }}
        </p>
      </div>
    </div>

    <!-- ── Connexion SSO ── -->
    <div v-if="!authStore.isAuthenticated" class="dh-body">
      <div class="dh-sso-section">
        <p class="dh-sso-info">
          La connexion à <strong>Compet</strong> est gérée par <strong>DartsHub</strong>.<br>
          Tu seras redirigé vers la page de connexion sécurisée.
        </p>
        <p v-if="!cookiesAccepted" class="dh-msg dh-msg--error">
          Veuillez accepter les
          <a href="#" class="dh-link" @click.prevent="openCookieBanner">cookies</a>
          pour vous connecter.
        </p>
        <button class="dh-btn dh-btn-primary" :disabled="!cookiesAccepted" @click="authStore.redirectToLogin">
          Se connecter avec DartsHub
        </button>
      </div>
    </div>

    <!-- ── Zone profil connecté ── -->
    <div v-else class="dh-body dh-profile">
      <div class="dh-avatar-wrap">
        <img
          :src="authStore.profilePictureUrl && authStore.profilePictureUrl !== '' ? authStore.profilePictureUrl : defaultAvatar"
          alt="Photo de profil" class="dh-avatar" />
      </div>
      <h3 class="dh-nickname">{{ authStore.nickname }}</h3>
      <div class="dh-user-details">
        <div class="dh-detail-row">
          <span class="dh-detail-label">Nom complet</span>
          <span>{{ authStore.name }} {{ authStore.firstname }}</span>
        </div>
        <div class="dh-detail-row">
          <span class="dh-detail-label">Email</span>
          <span>{{ authStore.email }}</span>
        </div>
        <div class="dh-detail-row">
          <span class="dh-detail-label">Bio</span>
          <span>{{ authStore.bio || '-' }}</span>
        </div>
      </div>

      <div class="dh-action-group">
        <button class="dh-btn dh-btn-secondary" @click="goToProfile">Modifier mon profil</button>
        <button class="dh-btn dh-btn-ghost" @click="handleLogout">Se déconnecter</button>
        <button class="dh-btn dh-btn-ghost" @click="authStore.closeAuthModal">Fermer</button>
      </div>
    </div>

    <!-- ── Pied DartsHub ── -->
    <div class="dh-footer">
      <span>Authentification sécurisée par</span>
      <strong>&nbsp;DartsHub</strong>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

/* ── Palette DartsHub — Pro Circuit ── */
/* Navy #1e3a5f  |  Or #eab308  |  Cyan #06b6d4 */

.dh-wrapper {
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Segoe UI', sans-serif;
  background: #ffffff;
  color: #1e293b;
  border-radius: 20px;
  overflow: hidden;
  width: 420px;
  max-width: 100%;
  min-height: 520px;
}

/* ── Header ── */
.dh-header {
  background: linear-gradient(145deg, #162d4a 0%, #1e3a5f 60%, #1e4d8f 100%);
  position: relative;
  text-align: center;
  overflow: hidden;
}

.dh-target-bg {
  position: absolute;
  top: 50%;
  right: -30px;
  transform: translateY(-50%);
  width: 240px;
  height: 240px;
  pointer-events: none;
  opacity: 0.6;
}

.dh-header-content {
  position: relative;
  z-index: 1;
  padding: 22px 28px 16px;
}

.dh-brand {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.dh-brand-logo {
  height: 2.1rem;
  width: auto;
}

.dh-accent-bar {
  display: flex;
  height: 4px;
  width: 110px;
  margin: 0 auto 12px;
  border-radius: 10px;
  overflow: hidden;
  gap: 3px;
}

.dh-accent-bar span { flex: 1; border-radius: 10px; }
.dh-accent-bar span:nth-child(1) { background: #eab308; }
.dh-accent-bar span:nth-child(2) { background: rgba(255, 255, 255, 0.9); }
.dh-accent-bar span:nth-child(3) { background: #06b6d4; }

.dh-subtitle {
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 14px;
  letter-spacing: 0.2px;
  font-weight: 300;
}

/* ── Corps ── */
.dh-body {
  padding: 24px 28px;
  flex: 1;
}

.dh-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dh-field label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.1px;
}

.dh-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e8ecf5;
  border-radius: 12px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 0.92rem;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  box-sizing: border-box;
}

.dh-input:focus {
  outline: none;
  border-color: #1e3a5f;
  box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.1);
  background: #ffffff;
}

/* ── Boutons ── */
.dh-btn {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  letter-spacing: 0.2px;
}

.dh-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.dh-btn-primary {
  background: linear-gradient(135deg, #1e3a5f 0%, #162d4a 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(30, 58, 95, 0.28);
}

.dh-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #162d4a 0%, #0f1e30 100%);
  box-shadow: 0 6px 18px rgba(30, 58, 95, 0.36);
  transform: translateY(-1px);
}

.dh-btn-secondary {
  background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%);
  color: #1e3a5f;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(234, 179, 8, 0.28);
}

.dh-btn-secondary:hover:not(:disabled) {
  background: linear-gradient(135deg, #ca8a04 0%, #a16207 100%);
  box-shadow: 0 6px 18px rgba(234, 179, 8, 0.36);
  transform: translateY(-1px);
}

.dh-btn-ghost {
  background: transparent;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.dh-btn-ghost:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
}

/* ── Messages ── */
.dh-msg {
  font-size: 0.83rem;
  font-weight: 500;
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  margin: 0;
}

.dh-msg--error {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

.dh-msg--success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

/* ── Liens ── */
.dh-link {
  color: #1e3a5f;
  font-weight: 600;
  text-decoration: none;
}

.dh-link:hover { text-decoration: underline; }

/* ── Profil ── */
.dh-profile { text-align: center; }

.dh-avatar-wrap {
  position: relative;
  width: 90px;
  margin: 0 auto 10px;
}

.dh-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #1e3a5f;
  box-shadow: 0 4px 16px rgba(30, 58, 95, 0.2);
}

.dh-nickname {
  margin: 0 0 14px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e3a5f;
}

.dh-user-details {
  background: #f8fafc;
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 16px;
  text-align: left;
  border: 1px solid #e8ecf5;
}

.dh-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 7px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.85rem;
  gap: 12px;
}

.dh-detail-row:last-child { border-bottom: none; }

.dh-detail-label {
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  min-width: 90px;
}

.dh-action-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Divider ── */
.dh-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px;
  color: #94a3b8;
  font-size: 0.78rem;
}
.dh-divider::before,
.dh-divider::after {
  content: '';
  flex: 1;
  border-top: 1px solid #e8ecf5;
}

/* ── Bouton Google ── */
.dh-btn-google {
  background: #ffffff;
  color: #3c4043;
  border: 2px solid #dadce0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.dh-btn-google:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #c6c9cc;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}


.dh-sso-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dh-sso-info {
  font-size: 0.82rem;
  color: #64748b;
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

.dh-sso-info strong { color: #1e3a5f; }

/* ── Footer ── */
.dh-footer {
  background: #f8fafc;
  border-top: 1px solid #e8ecf5;
  padding: 10px 16px;
  text-align: center;
  font-size: 0.72rem;
  color: #94a3b8;
}

.dh-footer strong {
  color: #1e3a5f;
  font-weight: 600;
}
</style>
